const DEFAULT_RETRY_DELAY_MS = 30_000;
const MAX_TIMEOUT_MS = 2_147_483_647;

export type AtAGlanceRetryReason = 'rateLimited' | 'transientServiceError';

export const getServiceRetryReason = (
	status: number,
	code: string | undefined,
	retryable: boolean | undefined,
): AtAGlanceRetryReason | undefined => {
	if (!retryable) {
		return undefined;
	}
	if (status === 503 && code === 'AI_FEATURE_RATE_LIMITED') {
		return 'rateLimited';
	}
	if (
		(status === 504 && code === 'AI_FEATURE_TIMEOUT') ||
		(status === 503 && code === 'AI_FEATURE_DEPENDENCY_FAILURE') ||
		(status === 502 &&
			(code === 'AI_FEATURE_EMPTY_RESPONSE' || code === 'AI_FEATURE_INVALID_RESPONSE'))
	) {
		return 'transientServiceError';
	}
	return undefined;
};

export const getRetryAfterDelay = (
	header: string | null,
	fallbackDelayMs: number = DEFAULT_RETRY_DELAY_MS,
): number => {
	if (header !== null && /^\d+$/.test(header.trim())) {
		const seconds = Number(header);
		if (Number.isSafeInteger(seconds) && seconds <= 2_147_483_647) {
			return seconds * 1000;
		}
	}
	return fallbackDelayMs;
};

// Long Retry-After values must not overflow the browser timer and retry immediately.
export const scheduleAfter = (notBefore: number, callback: () => void): (() => void) => {
	let timer: ReturnType<typeof setTimeout>;
	const schedule = () => {
		const remaining = notBefore - Date.now();
		if (remaining <= 0) {
			callback();
			return;
		}
		timer = setTimeout(schedule, Math.min(remaining, MAX_TIMEOUT_MS));
	};
	schedule();
	return () => clearTimeout(timer);
};

// Quota applies across payloads. Keep its deadline outside the request effect so
// refreshed connection data and byline remounts cannot bypass the server cooldown.
const quotaCooldowns = new Map<string, number>();

export const getQuotaCooldown = (cloudId: string): number => {
	const notBefore = quotaCooldowns.get(cloudId) ?? 0;
	if (notBefore <= Date.now()) {
		quotaCooldowns.delete(cloudId);
		return 0;
	}
	return notBefore;
};

export const recordQuotaCooldown = (cloudId: string, notBefore: number): void => {
	for (const [key, deadline] of quotaCooldowns) {
		if (deadline <= Date.now()) quotaCooldowns.delete(key);
	}
	quotaCooldowns.set(cloudId, Math.max(getQuotaCooldown(cloudId), notBefore));
};

export const clearQuotaCooldownsForTesting = (): void => {
	quotaCooldowns.clear();
};
